import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from './ui/Drawer';
import TypeAhead from './TypeAhead';
import { PermissionAwareButton } from '../contexts/PermissionAccessContext';
import { isValidEmailList } from '../utils/email';
import {
  ContactType,
  NotificationRow,
  defaultNotifications,
  getContact,
  createContact,
  updateContact,
} from '../services/contacts.service';
import { titleCase } from '../utils/titleCase';
import { formatPhoneMaybe } from '../utils/phone';

// Communication method — hardcoded options, stored uppercase.
const COMMUNICATION_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'FAX', label: 'Fax' },
  { value: 'ANY', label: 'Any' },
];

export interface ContactForm {
  contacttype: number | null;
  contactname: string;
  contacttitle: string;
  contactphone: string;
  contactfax: string;
  contactemail: string;
  communication: string;
  notes: string;
  deleted: string;
}

/** An unsaved contact held in memory (parent record not yet created). */
export interface PendingContact {
  form: ContactForm;
  notifications: NotificationRow[];
}

const EMPTY: ContactForm = {
  contacttype: null,
  contactname: '',
  contacttitle: '',
  contactphone: '',
  contactfax: '',
  contactemail: '',
  communication: '',
  notes: '',
  deleted: 'N',
};


const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
}> = ({ label, value, onChange, onBlur, disabled, required, invalid }) => (
  <div>
    <label className="block text-sm font-medium text-label mb-1">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-white ${
        invalid
          ? 'border-red-400 dark:border-red-500 focus:ring-red-500'
          : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
      }`}
    />
  </div>
);



const FLAG_COLS: { key: keyof NotificationRow; label: string }[] = [
  { key: 'checked', label: 'On' },
  { key: 'csr', label: 'CSR' },
  { key: 'whsecsr', label: 'Whse CSR' },
  { key: 'dispatcher', label: 'Dispatcher' },
];

interface ContactInfoDrawerProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  contactRecno: number | null;
  tablename: string;
  tablerecno: number;
  contactTypes: ContactType[];
  onClose: () => void;
  onSaved?: () => void;
  /** When set, the contact is unsaved and parent-managed: editing this in-memory
   *  contact instead of fetching by recno. */
  pendingInitial?: PendingContact | null;
  /** When provided, save hands the contact back to the parent (to stash) instead
   *  of hitting the API. Used while the parent record is still being created. */
  onSubmit?: (data: PendingContact) => void;
}

export const ContactInfoDrawer: React.FC<ContactInfoDrawerProps> = ({
  isOpen,
  mode,
  contactRecno,
  tablename,
  tablerecno,
  contactTypes,
  onClose,
  onSaved,
  pendingInitial,
  onSubmit,
}) => {
  const [form, setForm] = useState<ContactForm>(EMPTY);
  const [notifications, setNotifications] = useState<NotificationRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  // showErrors reveals validation feedback after a blocked save; apiError holds
  // load/save failures. Anchor at the top so we can scroll the banner into view.
  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const set = useCallback(<K extends keyof ContactForm>(key: K, value: ContactForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  // Live validation — drives the error banner and per-field red borders, so both
  // clear as the user fixes each field.
  const validation = useMemo(() => {
    const errors: string[] = [];
    const invalid = new Set<string>();
    if (!form.contactname.trim()) { errors.push('Contact name is required.'); invalid.add('contactname'); }
    if (!form.contacttype) { errors.push('Contact type is required.'); invalid.add('contacttype'); }
    if (!isValidEmailList(form.contactemail, { semicolon: true })) {
      errors.push('Email is not valid. Separate multiple addresses with commas or semicolons.');
      invalid.add('contactemail');
    }
    const badIdx = notifications.findIndex((n) => !isValidEmailList(n.email, { semicolon: true }));
    if (badIdx >= 0) {
      errors.push(`Notification email for "${notifications[badIdx].notificationtype}" is not valid. Separate multiple addresses with commas or semicolons.`);
      invalid.add(`notif-${badIdx}`);
    }
    return { errors, invalid };
  }, [form, notifications]);

  const bannerErrors = [...(showErrors ? validation.errors : []), ...(apiError ? [apiError] : [])];

  useEffect(() => {
    if (showErrors || apiError) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [showErrors, apiError]);

  useEffect(() => {
    if (!isOpen) return;
    setShowErrors(false);
    setApiError(null);
    if (pendingInitial) {
      // Editing an in-memory (unsaved) contact.
      setForm({ ...pendingInitial.form });
      setNotifications(pendingInitial.notifications.length ? pendingInitial.notifications : defaultNotifications());
    } else if (mode === 'edit' && contactRecno != null) {
      setLoading(true);
      getContact(contactRecno)
        .then(({ contact, notifications }) => {
          setForm({
            contacttype: contact.contacttype || null,
            contactname: contact.contactname,
            contacttitle: contact.contacttitle,
            contactphone: contact.contactphone,
            contactfax: contact.contactfax,
            contactemail: contact.contactemail,
            communication: contact.communication,
            notes: contact.notes || '',
            deleted: contact.deleted || 'N',
          });
          setNotifications(notifications.length ? notifications : defaultNotifications());
        })
        .catch((err) => {
          console.error('Failed to load contact:', err);
          setApiError(err?.response?.data?.detail || 'Could not load contact.');
        })
        .finally(() => setLoading(false));
    } else {
      setForm({ ...EMPTY });
      setNotifications(defaultNotifications());
    }
  }, [isOpen, mode, contactRecno, pendingInitial]);

  const setNotif = useCallback((idx: number, key: keyof NotificationRow, value: string) => {
    setNotifications((prev) => prev.map((n, i) => (i === idx ? { ...n, [key]: value } : n)));
  }, []);

  const handleSave = useCallback(async () => {
    if (validation.errors.length) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setApiError(null);
    // Parent-managed (unsaved parent record): hand the contact back to stash.
    if (onSubmit) {
      onSubmit({ form, notifications });
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      const payload = { ...form, notifications };
      if (mode === 'create') {
        await createContact({ tablename, tablerecno, ...payload });
      } else {
        await updateContact(contactRecno!, payload);
      }
      onSaved?.();
      onClose();
    } catch (err: any) {
      console.error('Failed to save contact:', err);
      setApiError(err?.response?.data?.detail || err?.message || 'Unknown error occurred');
    } finally {
      setIsSaving(false);
    }
  }, [validation, form, notifications, onSubmit, mode, contactRecno, tablename, tablerecno, onClose, onSaved]);

  const typeOptions = useMemo(
    () => contactTypes.map((t) => ({ value: String(t.recno), label: titleCase(t.type) })),
    [contactTypes]
  );

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title={mode === 'create' ? 'Add Contact' : `Edit Contact — ${form.contactname || ''}`}
        position="right"
        width="50rem"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <PermissionAwareButton
              onClick={handleSave}
              disabled={isSaving || loading}
              className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  {mode === 'create' ? 'Create Contact' : 'Save Changes'}
                </>
              )}
            </PermissionAwareButton>
          </div>
        }
      >
        <div className="space-y-6">
          <div ref={topRef} />
          {bannerErrors.length > 0 && (
            <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
              <p className="text-sm font-semibold text-red-800 dark:text-red-300">Please fix the following before saving:</p>
              <ul className="mt-1 list-disc list-inside text-sm text-red-700 dark:text-red-300">
                {bannerErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Contact fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-label mb-1">
                Contact Type <span className="text-red-500">*</span>
              </label>
              <div className={showErrors && validation.invalid.has('contacttype') ? 'rounded-lg ring-2 ring-red-400' : ''}>
                <TypeAhead
                  options={typeOptions}
                  value={form.contacttype != null ? String(form.contacttype) : ''}
                  onChange={(v) => set('contacttype', v ? Number(v) : null)}
                  onClear={() => set('contacttype', null)}
                  placeholder="Select…"
                  disabled={isSaving}
                  showSearch
                  flagInvalidValue
                />
              </div>
            </div>
            <Field label="Name" value={form.contactname} onChange={(v) => set('contactname', v)} disabled={isSaving} required invalid={showErrors && validation.invalid.has('contactname')} />
            <Field label="Title" value={form.contacttitle} onChange={(v) => set('contacttitle', v)} disabled={isSaving} />
            <Field label="Email" value={form.contactemail} onChange={(v) => set('contactemail', v)} disabled={isSaving} invalid={showErrors && validation.invalid.has('contactemail')} />
            <Field
              label="Phone"
              value={form.contactphone}
              onChange={(v) => set('contactphone', v)}
              onBlur={() => set('contactphone', formatPhoneMaybe(form.contactphone))}
              disabled={isSaving}
            />
            <Field
              label="Fax"
              value={form.contactfax}
              onChange={(v) => set('contactfax', v)}
              onBlur={() => set('contactfax', formatPhoneMaybe(form.contactfax))}
              disabled={isSaving}
            />
            <div>
              <label className="block text-sm font-medium text-label mb-1">Communication</label>
              <TypeAhead
                options={COMMUNICATION_OPTIONS}
                value={form.communication}
                onChange={(v) => set('communication', v as string)}
                onClear={() => set('communication', '')}
                placeholder="Select…"
                disabled={isSaving}
                showSearch
                flagInvalidValue
              />
            </div>
          </div>

          <label className="block text-sm font-medium text-label">
            Notes
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              disabled={isSaving || loading}
              rows={3}
              className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-normal"
            />
          </label>

          {/* Notifications */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-body border-b border-gray-200 dark:border-gray-700 pb-1">
              Notifications
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted text-left">
                    <th className="py-1 pr-2 font-medium">Type</th>
                    {FLAG_COLS.map((c) => (
                      <th key={c.key} className="py-1 px-2 font-medium text-center">{c.label}</th>
                    ))}
                    <th className="py-1 pl-2 font-medium">Email override</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((n, idx) => (
                    <tr key={n.recno ?? n.notificationtype} className="border-t border-gray-100 dark:border-gray-700/60">
                      <td className="py-1.5 pr-2 text-label whitespace-nowrap">{titleCase(n.notificationtype)}</td>
                      {FLAG_COLS.map((c) => (
                        <td key={c.key} className="py-1.5 px-2 text-center">
                          <input
                            type="checkbox"
                            checked={n[c.key] === 'Y'}
                            onChange={(e) => setNotif(idx, c.key, e.target.checked ? 'Y' : 'N')}
                            disabled={isSaving}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </td>
                      ))}
                      <td className="py-1.5 pl-2">
                        <input
                          type="text"
                          value={n.email}
                          onChange={(e) => setNotif(idx, 'email', e.target.value)}
                          disabled={isSaving}
                          placeholder="(use contact email)"
                          className={`w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 dark:bg-gray-700 dark:text-white ${
                            showErrors && validation.invalid.has(`notif-${idx}`)
                              ? 'border-red-400 dark:border-red-500 focus:ring-red-500'
                              : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                          }`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Status — delete/restore an existing (persisted) contact. */}
          {!onSubmit && mode === 'edit' && contactRecno != null && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.deleted === 'Y'}
                  onChange={(e) => set('deleted', e.target.checked ? 'Y' : 'N')}
                  disabled={isSaving}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-label">Deleted</span>
              </label>
              <p className="mt-1 ml-7 text-xs text-muted">
                Removes this contact from the location. Uncheck and save to restore it.
              </p>
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default ContactInfoDrawer;
